// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { browserSupportsWebAuthn, WebAuthnError } from "@simplewebauthn/browser";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Fingerprint, Loader2 } from "lucide-react";
import { useTheme } from "next-themes";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { FocalLogoIcon } from "@/components/icons";
import { LanguageSwitcher } from "@/components/layout/language-switcher";
import { ThemeToggle } from "@/components/layout/theme-toggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { AuthError, authClient } from "@/lib/auth/auth-client";
import { DEFAULT_AUTHENTICATED_PATH } from "@/lib/routes";

export const Route = createFileRoute("/login")({
  beforeLoad: () => {
    const token = localStorage.getItem("access_token");
    if (token) {
      throw redirect({ to: "/" });
    }
  },
  validateSearch: z.object({ redirect: z.string().optional(), state: z.string().optional() }),
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation("login");
  const { resolvedTheme } = useTheme();
  const { login, loginWithMfa, loginWithSSO, loginWithPasskey, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { redirect: redirectTo, state: ssoState } = Route.useSearch();
  const postLoginTo = redirectTo ?? DEFAULT_AUTHENTICATED_PATH;
  const supportsPasskey = browserSupportsWebAuthn();

  const [step, setStep] = useState<"email" | "password" | "mfa" | "change-password" | "sso-pending">("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [tempToken, setTempToken] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);

  const greetingIndex = useMemo(() => Math.floor(Math.random() * 6), []);

  // Shimmer effect on logo icon at random intervals (40-120s)
  const logoRef = useRef<HTMLDivElement>(null);
  useEffect(function scheduleLogoShimmerAnimation() {
    let timeout: ReturnType<typeof setTimeout>;
    function scheduleShimmer() {
      const delay = (30 + Math.random() * 90) * 1000;
      timeout = setTimeout(() => {
        const el = logoRef.current;
        if (el) {
          el.classList.add("shimmer-active");
          el.addEventListener("animationend", () => el.classList.remove("shimmer-active"), { once: true });
        }
        scheduleShimmer();
      }, delay);
    }
    scheduleShimmer();
    return () => clearTimeout(timeout);
  }, []);

  // SSO callback: the IdP redirects back to /login?state=… . Claim the one-time state exactly once
  // (the token is single-use — StrictMode's double render or any re-render must not re-POST it).
  const ssoClaimAttempted = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: ssoState is the one-time trigger; the claim is ref-guarded so navigate/loginWithSSO/postLoginTo (which change identity across renders) must not re-fire it.
  useEffect(
    function claimSsoStateOnce() {
      if (!ssoState || ssoClaimAttempted.current) return;
      ssoClaimAttempted.current = true;
      loginWithSSO(ssoState)
        .then(() => navigate({ to: postLoginTo }))
        .catch((err) => {
          setError(loginErrorMessage(err, "other"));
          // Drop the now-dead one-time state token from the URL so a refresh/retry doesn't re-claim it.
          navigate({ to: "/login", search: (prev) => ({ ...prev, state: undefined }), replace: true });
        });
    },
    [ssoState],
  );

  // If already authenticated, redirect to home
  if (isAuthenticated) {
    navigate({ to: postLoginTo });
    return null;
  }

  // Map an auth failure to a user-facing message. AuthError.message carries the backend reason
  // (errors[0].detail): account_locked / access_not_granted / unauthorized. The "verify credentials"
  // wording only makes sense on the password flow; mfa keeps its own invalid-code message.
  function loginErrorMessage(err: unknown, flow: "password" | "mfa" | "other"): string {
    if (err instanceof AuthError) {
      if (err.message === "account_locked") return t("errors.accountLocked");
      if (err.message === "access_not_granted") return t("errors.accessNotGranted");
      if (err.message === "sso_required") return t("errors.ssoRequired");
      if (err.message === "unauthorized") {
        if (flow === "password") return t("errors.invalidCredentials");
        if (flow === "mfa") return t("mfa.invalidCode");
        return t("errors.unauthorized");
      }
    }
    if (flow === "mfa") return t("mfa.invalidCode");
    return err instanceof Error ? err.message : t("errors.unauthorized");
  }

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await authClient.getRealm(email);
      if (res.sso && res.redirect) {
        // SSO redirect -- open in same window
        window.location.href = res.redirect;
        setStep("sso-pending");
      } else if (res.hasPasskey && supportsPasskey) {
        // Auto-trigger passkey with this user's credentials; fall back to password on cancel/failure.
        try {
          await loginWithPasskey(email);
          navigate({ to: postLoginTo });
          return;
        } catch {
          setStep("password");
        }
      } else {
        setStep("password");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to check realm");
    } finally {
      setLoading(false);
    }
  }

  async function handlePasskeyLogin() {
    setError("");
    setPasskeyLoading(true);

    try {
      // No email -- discoverable credentials; the browser shows all passkeys for this RP.
      await loginWithPasskey();
      navigate({ to: postLoginTo });
    } catch (err) {
      // User-driven cancellation is not an error worth showing: an aborted ceremony
      // (ERROR_CEREMONY_ABORTED) or a dismissed / timed-out prompt (NotAllowedError, which v13 wraps
      // as ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY) stays silent. A genuine WebAuthn/browser failure shows
      // the passkey message; a backend rejection (locked, not granted, …) surfaces its mapped reason.
      if (err instanceof WebAuthnError) {
        const cancelled = err.code === "ERROR_CEREMONY_ABORTED" || err.code === "ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY";
        if (!cancelled) setError(t("passkey.error"));
      } else if (err instanceof AuthError && err.message === "unknown_credential") {
        // The signed passkey isn't one the backend recognises (revoked, or from another environment).
        setError(t("passkey.unknownCredential"));
      } else {
        setError(loginErrorMessage(err, "other"));
      }
    } finally {
      setPasskeyLoading(false);
    }
  }

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const result = await login({ email, password });
      if (result?.action === "twoFactor") {
        setTempToken(result.tempToken);
        setStep("mfa");
      } else if (result?.action === "changePassword") {
        // Forced password change: the backend won't issue a session until the password is reset.
        setTempToken(result.tempToken);
        setStep("change-password");
      } else {
        navigate({ to: postLoginTo });
      }
    } catch (err) {
      setError(loginErrorMessage(err, "password"));
    } finally {
      setLoading(false);
    }
  }

  function resetChangePasswordFields() {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
  }

  // Map a password-change failure. A 403 "unauthorized" means the current password was wrong; any other
  // backend reason (policy: too short, reused, etc.) is surfaced as-is since it's the actionable detail.
  function changePasswordErrorMessage(err: unknown): string {
    if (err instanceof AuthError) {
      if (err.message === "unauthorized") return t("changePassword.wrongCurrent");
      return err.message || t("changePassword.error");
    }
    return err instanceof Error ? err.message : t("changePassword.error");
  }

  async function handleChangePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (newPassword !== confirmNewPassword) {
      setError(t("changePassword.mismatch"));
      return;
    }

    setLoading(true);
    try {
      // The temp token from the changePassword login response authorizes this call before a session exists.
      await authClient.changePassword(currentPassword, newPassword, tempToken);
      // Password is set — re-authenticate with it. This naturally chains into the MFA step for accounts
      // that require it, and clears the forced-change gate for everyone else.
      const result = await login({ email, password: newPassword });
      if (result?.action === "twoFactor") {
        setTempToken(result.tempToken);
        setStep("mfa");
        setPassword("");
        resetChangePasswordFields();
      } else if (result?.action === "changePassword") {
        // Defensive: the backend still demands a change. Don't loop — surface an error.
        setError(t("changePassword.error"));
      } else {
        navigate({ to: postLoginTo });
      }
    } catch (err) {
      setError(changePasswordErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleMfaSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await loginWithMfa(mfaCode, tempToken);
      navigate({ to: postLoginTo });
    } catch (err) {
      // A wrong/expired code comes back as "unauthorized" → invalid-code message; a lock/access
      // change between steps surfaces its own reason.
      setError(loginErrorMessage(err, "mfa"));
      setMfaCode("");
    } finally {
      setLoading(false);
    }
  }

  // While the SSO state claim is in flight, show a full-screen spinner. On failure we fall through to
  // the normal login form so the mapped error is visible and the user can retry with credentials.
  if (ssoState && !error) {
    return (
      <div className="flex h-svh items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="grid h-svh lg:grid-cols-2">
      {/* Left — sign-in form */}
      <div className="relative flex items-center justify-center p-8">
        <div className="absolute bottom-4 right-4 lg:bottom-10 lg:right-10 flex items-center gap-1">
          <LanguageSwitcher />
          <ThemeToggle />
        </div>
        <div className="mx-auto flex w-full max-w-sm flex-col gap-6 [&_input::placeholder]:opacity-50">
          <div className="flex flex-col items-center gap-2 text-center">
            <div
              ref={logoRef}
              className="logo-shimmer flex h-12 w-12 items-center justify-center rounded-lg bg-primary"
            >
              <FocalLogoIcon width={24} height={24} className="text-primary-foreground" />
            </div>
            <h1 className="text-2xl font-medium tracking-tight">{t("appTitle")}</h1>
            <p className="text-sm text-muted-foreground">
              {(t("greetings", { returnObjects: true }) as string[])[greetingIndex]}
            </p>
          </div>

          {step === "email" && (
            <div className="space-y-4">
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t("email")}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="user@focal.systems"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
                <Button type="submit" className="w-full" disabled={loading || passkeyLoading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t("continue")}
                </Button>
              </form>
              {supportsPasskey && (
                <>
                  <div className="relative text-center text-sm text-muted-foreground">
                    <span className="bg-background relative z-10 px-2">{t("or")}</span>
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t" />
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full gap-2 font-normal"
                    disabled={loading || passkeyLoading}
                    onClick={handlePasskeyLogin}
                  >
                    {passkeyLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Fingerprint className="h-4 w-4" />
                    )}
                    {t("passkey.signIn")}
                  </Button>
                </>
              )}
            </div>
          )}

          {step === "password" && (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email-display">{t("email")}</Label>
                <Input id="email-display" type="email" value={email} disabled />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t("password")}</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <div className="space-y-2">
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t("sign-in")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full font-normal"
                  onClick={() => {
                    setStep("email");
                    setPassword("");
                    setError("");
                  }}
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t("back")}
                </Button>
              </div>
            </form>
          )}

          {step === "mfa" && (
            <form onSubmit={handleMfaSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>{t("mfa.label")}</Label>
                <InputOTP maxLength={6} value={mfaCode} onChange={setMfaCode} autoFocus containerClassName="w-full">
                  <InputOTPGroup className="w-full">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <InputOTPSlot key={i} index={i} className="h-16 flex-1 text-4xl md:text-4xl" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
                <p className="text-xs text-muted-foreground">{t("mfa.hint")}</p>
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <div className="space-y-2">
                <Button type="submit" className="w-full" disabled={loading || mfaCode.length < 6}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t("mfa.verify")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full font-normal"
                  onClick={() => {
                    setStep("email");
                    setPassword("");
                    setMfaCode("");
                    setTempToken("");
                    setError("");
                  }}
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t("back")}
                </Button>
              </div>
            </form>
          )}

          {step === "change-password" && (
            <form onSubmit={handleChangePasswordSubmit} className="space-y-4">
              <p className="text-sm text-muted-foreground">{t("changePassword.description")}</p>
              <div className="space-y-2">
                <Label htmlFor="current-password">{t("changePassword.current")}</Label>
                <Input
                  id="current-password"
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">{t("changePassword.new")}</Label>
                <Input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-new-password">{t("changePassword.confirm")}</Label>
                <Input
                  id="confirm-new-password"
                  type="password"
                  autoComplete="new-password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  required
                />
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <div className="space-y-2">
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t("changePassword.submit")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full font-normal"
                  onClick={() => {
                    setStep("email");
                    setPassword("");
                    setTempToken("");
                    resetChangePasswordFields();
                    setError("");
                  }}
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t("back")}
                </Button>
              </div>
            </form>
          )}

          {step === "sso-pending" && (
            <div className="flex flex-col items-center gap-4 py-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">{t("sso-redirecting")}</p>
            </div>
          )}
        </div>
      </div>

      {/* Right — branding panel */}
      <link rel="preload" as="image" href="/backoffice-login-bg-light.webp" />
      <link rel="preload" as="image" href="/backoffice-login-bg-dark.webp" />
      <div
        className="relative hidden overflow-hidden lg:flex lg:flex-col lg:justify-between lg:border-l lg:p-10"
        style={{
          backgroundImage: `url(${resolvedTheme === "dark" ? "/backoffice-login-bg-dark.webp" : "/backoffice-login-bg-light.webp"})`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent from-50% to-black/50" />
        <div className="relative">
          <img
            src="/focal-logo.svg"
            alt="Focal Systems"
            width={180}
            height={50}
            className={resolvedTheme === "dark" ? "brightness-0 invert" : ""}
          />
        </div>
        <div className="relative">
          <p className="text-sm text-white/80">AI-powered retail analytics for smarter store operations.</p>
          <p className="mt-1 text-xs text-white/40">
            v{__APP_VERSION__} Arcadia ({__GIT_SHA__})
            {import.meta.env.VITE_APP_ENV ? ` · ${import.meta.env.VITE_APP_ENV}` : ""}
          </p>
        </div>
      </div>
    </div>
  );
}
